import { FC, memo, useEffect } from 'react';
import styled from 'styled-components';

import { CommonCard } from '../CommonCard';
import { CardId } from '../CardId';
import { CardToGo } from '../CardTogo';
import { SelectedText } from '../SelectedText';
import { CardNumber30 } from '../CardNumber30';
import { BingoCard75Props as BingoCard30Props } from '../BingoCard75';
import { useCalculateTogo } from '../../hooks/use-calculate-togo';
import { useSetGlowEffectsRx } from '../../hooks/use-set-glow-effects-rx';

const ARRAY = [...Array(3)];

export const BingoCard30Container = styled(CommonCard)<{
    picked: boolean;
    bought: boolean;
    pickCards: boolean;
}>`
    height: 22.5rem;
    width: 18rem;

    ${({ theme: { pickedCards }, picked, bought, pickCards }) => `
        position: relative;
        ${pickCards ? `cursor: pointer;` : ''}
        ${
            bought
                ? `
                box-shadow: 0 0.25rem 0 0 ${pickedCards.boxShadow.bought.first}, inset 0 0.125rem 0 0 ${pickedCards.boxShadow.bought.second};
                background-color: ${pickedCards.background.bought};
            `
                : picked
                ? `
                box-shadow: 0 0.25rem 0 0 ${pickedCards.boxShadow.picked.first}, inset 0 0.125rem 0 0 ${pickedCards.boxShadow.picked.second};
                background-color: ${pickedCards.background.picked};
                `
                : ''
        }
    `};
`;

export const CardNumberContainer = styled.div`
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    justify-items: center;
`;

export const CardRow = styled.div`
    display: grid;
    grid-template-rows: repeat(3, 1fr);
    justify-items: center;
    grid-row-gap: 0.625rem;
`;

export const CardTextContainer = styled.div`
    display: grid;
    grid-template-columns: repeat(5, 1fr);
    justify-items: center;
    margin-bottom: 0.8rem;
`;

export const CardTextOverlayContainer = styled(CardTextContainer)`
    position: absolute;

    left: 0.5rem;
    right: 0.5rem;
`;

export const CardText = styled.div`
    font-family: Mitr;
    font-size: 2.25rem;
    font-weight: 600;
    font-stretch: normal;
    font-style: normal;
    line-height: 0.81;
    letter-spacing: normal;
    text-align: center;

    ${({ theme: { cardText } }) => `
        color: ${cardText.color};
        text-shadow: 0 0.125rem ${cardText.textShadow}, 0 0.063rem ${cardText.color};
    `};
`;

export const CardFooterContainer = styled.div`
    display: flex;
    justify-content: flex-end;
    align-items: center;
    position: relative;
    margin: 0.625rem 0;
`;

export const BingoCard30: FC<BingoCard30Props> = memo(
    ({
        gameType,
        tiles,
        cardId,
        autoDaub,
        hasFreeSpace,
        cardIndex,
        stripIndex,
        isStripped,
        dauberColor,
        dauberStyle,
        matches = 0,
        currentPart = 0,
        called = [],
        patterns = [],
        picked = false,
        bought = false,
        confirmedWinner = false,
        pickCards = false,
        fireBall,
        fireBallSnuffed,
        handleClick,
        ...props
    }) => {
        const setGlowEffectRx = useSetGlowEffectsRx();
        const { cardType, lastNumber } = useCalculateTogo(
            gameType,
            tiles,
            patterns,
            matches,
            called,
            currentPart,
            hasFreeSpace,
            confirmedWinner
        );

        useEffect(() => setGlowEffectRx(), [cardType, setGlowEffectRx]);

        return (
            <BingoCard30Container
                cardType={pickCards ? 'default' : cardType}
                picked={picked}
                bought={bought}
                pickCards={pickCards}
                onClick={
                    pickCards && typeof cardIndex === 'number' && handleClick
                        ? () => handleClick(cardIndex, stripIndex)
                        : undefined
                }
                {...props}
                className={`${props.className} glow-effects`}
            >
                {cardType === 'oneTG' && (
                    <svg className="glow-container">
                        <filter id="blur">
                            <feGaussianBlur
                                stdDeviation="6"
                                edgeMode="duplicate"
                            />
                        </filter>
                        <rect
                            className="glow-blur"
                            strokeLinecap="round"
                            filter='url("#blur")'
                            pathLength={1000}
                        ></rect>
                        <rect
                            className="glow-line"
                            strokeLinecap="round"
                            pathLength={1000}
                        ></rect>
                        <rect
                            className="glow-blur-2"
                            strokeLinecap="round"
                            filter='url("#blur")'
                            pathLength={1000}
                        ></rect>
                        <rect
                            className="glow-line-2"
                            strokeLinecap="round"
                            pathLength={1000}
                        ></rect>
                    </svg>
                )}
                <CardTextContainer>
                    <CardText className={`${cardType}-BINGO-glow`}>B</CardText>
                    <CardText className={`${cardType}-BINGO-glow`}>I</CardText>
                    <CardText className={`${cardType}-BINGO-glow`}>N</CardText>
                    <CardText className={`${cardType}-BINGO-glow`}>G</CardText>
                    <CardText className={`${cardType}-BINGO-glow`}>O</CardText>
                </CardTextContainer>
                {/* Overlay for BINGO sequencial animation */}
                {cardType === 'oneTG' && (
                    <CardTextOverlayContainer>
                        <CardText className={`${cardType}-B-glow`}>B</CardText>
                        <CardText className={`${cardType}-I-glow`}>I</CardText>
                        <CardText className={`${cardType}-N-glow`}>N</CardText>
                        <CardText className={`${cardType}-G-glow`}>G</CardText>
                        <CardText className={`${cardType}-O-glow`}>O</CardText>
                    </CardTextOverlayContainer>
                )}
                <CardNumberContainer>
                    {ARRAY.map((_vRow, row) => (
                        <CardRow key={row}>
                            {ARRAY.map((_vCol, ci) => {
                                const index = row * ARRAY.length + ci;
                                return (
                                    <CardNumber30
                                        key={ci}
                                        autoDaub={autoDaub}
                                        dauberColor={dauberColor}
                                        dauberStyle={dauberStyle}
                                        isCalled={called.includes(tiles[index])}
                                        isFireBall={
                                            !!fireBall &&
                                            tiles[index] === fireBall
                                        }
                                        isFireBallSnuffed={fireBallSnuffed}
                                        bounce={lastNumber.includes(
                                            tiles[index]
                                        )}
                                    >
                                        {tiles[index]}
                                    </CardNumber30>
                                );
                            })}
                        </CardRow>
                    ))}
                </CardNumberContainer>
                <CardFooterContainer>
                    {pickCards && (picked || bought) ? (
                        <SelectedText type={bought ? 'bought' : 'picked'} />
                    ) : (
                        <CardId isStripped={isStripped}>
                            {cardId.split('-').pop()}
                        </CardId>
                    )}
                    <CardToGo
                        cardType={pickCards ? 'default' : cardType}
                        is30Ball={true}
                    />
                </CardFooterContainer>
            </BingoCard30Container>
        );
    }
);
